<template>
    <div class="flex h-full items-center justify-end ">
        <div v-if="showDeleteConfirmation">
            <UButton
                data-cy="confirm-delete-transaction"
                size="xs"
                color="error"
                class="mr-1"
                @click.stop="handleDelete"
            >
                Slett bilag?
            </UButton>
            <UButton
                size="xs"
                color="neutral"
                variant="subtle"
                @click.stop="showDeleteConfirmation = false"
            >
                Avbryt
            </UButton>
        </div>
        <div v-else class="flex items-center">
            <transaction-link
                v-if="!isTransactionView"
                :transaction-uuid="transaction.uuid"
                title="Vis detaljer"
                :data-cy="`inspect-transaction-${transaction.uuid}`"
                class="text-lg inline-block i-ph-file-magnifying-glass hover:text-primary mr-1"
            />
            <button
                v-if="!transaction.deleted && !isTransactionView"
                title="Kopier bilag"
                class="text-lg i-ph:copy hover:text-primary mr-1"
                :data-cy="`copy-transaction-${transaction.uuid}`"
                @click.stop="() => openCopyTransaction(transaction)"
            />
            <button
                v-if="!transaction.deleted && !outgoingInvoice"
                title="Endre bilag"
                class="text-lg i-ph:pencil hover:text-primary"
                :data-cy="`update-transaction-${transaction.uuid}`"
                @click.stop="() => openEditTransaction(transaction)"
            />
            <targeting-router-link
                v-else-if="outgoingInvoice"
                title="Endre faktura"
                class="text-lg i-ph:pencil hover:text-primary"
                :data-cy="`invoice-link-${transaction.uuid}`"
                :to="linkTarget"
            >
                <slot/>
            </targeting-router-link>
            <button
                v-if="!transaction.deleted && !outgoingInvoice"
                title="Slett bilag"
                class="text-lg i-ph-trash-fill hover:text-primary"
                :data-cy="`delete-transaction-${transaction.uuid}`"
                @click.stop="showDeleteConfirmation = true"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, provide, ref} from 'vue'
import TransactionLink from '@/transaction/TransactionLink.vue'
import {usePanelManager} from '@tritt/multipanel'
import {TRANSACTIONS} from '@/transaction'
import emitter from '@/services/infrastructure/emitter'
import {DELETE_TRANSACTION} from '@/types/transaction/TransactionEvent'
import {useEditTransactionCreator} from '@/transaction/edit-transaction/EditTransaction.service'
import {deleteTransaction} from '@/transaction/transaction.service'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import { TargetingRouterLink } from '@tritt/multipanel'
import {INVOICE_VIEW} from '@/invoice'

const {currentPanelContent} = usePanelManager()
const {openEditTransaction, openCopyTransaction} = useEditTransactionCreator()

const showDeleteConfirmation = ref(false)

const props = defineProps({
    params: {
        type: Object as () => any,
        required: true
    }
})

provide('panelId', props.params.context.panelId)
provide('panelPathSegment', props.params.context.panelPathSegment)

const transaction = computed(() => {
    return props.params.data
})

const outgoingInvoice = computed<boolean>(() => {
    const lines = transaction.value?.transactionLines ?? []
    return lines.some((line: TransactionLine) => {
        return !!line.invoiceUuid && line.accountNo.startsWith('3')
    })
})

const invoiceUuid = computed(() =>
    transaction.value?.transactionLines
        .find((line: TransactionLine) => !!line.invoiceUuid)
        ?.invoiceUuid
)

const linkTarget = computed(() => {
    return {
        componentName: INVOICE_VIEW.componentName,
        queryValues: {
            invoiceUuid: invoiceUuid.value
        }
    }
})

const isTransactionView = computed(() => {
    return currentPanelContent.value?.componentName === TRANSACTIONS.componentName
})

const handleDelete = async () => {
    await deleteTransaction(transaction.value.uuid)
    emitter.emit(DELETE_TRANSACTION, transaction.value)
    showDeleteConfirmation.value = false
}
</script>

<style scoped>

</style>